import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { PLATFORM_VOLUME_KEY, storageAPI } from '../StorageAPI';
import { authenticationAtom } from '../State';

/**
 * Ein Blick in die Datei, ohne sie zu öffnen.
 *
 * Nur für das, was ein Browser von sich aus zeichnen kann: Bilder als Bild, PDFs als erste Seite.
 * Alles andere bekommt keins — ein Vorschaubild für eine Tabelle oder ein CAD-Modell müsste
 * serverseitig gerendert werden, und ein graues Feld mit dem Wort „keine Vorschau" ist schlechter
 * als gar keins.
 *
 * Geladen wird erst, wenn eine Datei gewählt ist, und nur diese eine. Die Ablage liefert die Datei
 * ganz — ein Vorschaubild ist hier also die vollständige Datei, klein gezeichnet. Das ist für ein
 * paar hundert Kilobyte in Ordnung und für ein 80-MB-PDF nicht, weshalb oberhalb einer Grenze
 * nichts geladen wird.
 */

/** Ab hier wird nicht mehr geladen. Eine Vorschau ist kein Grund, ein halbes Hundert Megabyte zu ziehen. */
const MAX_PREVIEW_BYTES = 8 * 1024 * 1024;

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];

const extensionOf = (path: string) => (path.split('.').pop() ?? '').toLowerCase();

/** Was sich zeichnen lässt — und womit. */
export const previewKindOf = (path: unknown, size?: unknown): 'image' | 'pdf' | null => {
    if (typeof path !== 'string' || !path.includes('.')) return null;

    const bytes = typeof size === 'number' ? size : Number(size);
    if (Number.isFinite(bytes) && bytes > MAX_PREVIEW_BYTES) return null;

    const extension = extensionOf(path);

    if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
    if (extension === 'pdf') return 'pdf';

    return null;
};

export default function FileThumbnail({
    path,
    size,
    /**
     * Den ganzen Platz nehmen, statt eine feste Höhe mitzubringen.
     *
     * Wo der Leser die Aufteilung selbst verschieben kann, wäre eine feste Höhe eine zweite,
     * widersprechende Antwort auf die Frage, wie groß die Vorschau sein soll: Der Bereich würde
     * größer und das Bild bliebe, wo es war.
     */
    fill,
    /**
     * Angeklickt — mit dem Verweis auf die schon geladene Datei.
     *
     * Der Verweis geht mit, statt dass der Betrachter selbst lädt: Die Datei ist hier bereits ganz
     * geholt worden, und ein zweiter Gang über die Leitung führte zu demselben Ergebnis. Ohne
     * Rückruf bleibt das Bild ein Bild und ist nicht anklickbar.
     */
    onOpen,
}: {
    path: string;
    size?: number;
    fill?: boolean;
    onOpen?: (url: string) => void;
}) {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const [url, setUrl] = useState<string | undefined>();
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    const kind = previewKindOf(path, size);

    useEffect(() => {
        if (!kind || !authentication?.customer) {
            setUrl(undefined);
            return;
        }

        let cancelled = false;
        let created: string | undefined;

        setLoading(true);
        setFailed(false);

        storageAPI
            .downloadFile(authentication.customer, PLATFORM_VOLUME_KEY, path)
            .then(({ blob }) => {
                if (cancelled) return;

                // Der Browser zeichnet den Verweis nach dem Typ des Blobs, nicht nach der Endung. Ein
                // „.pdf" mit text/html im Antwortkopf würde im Rahmen als Seite mit den Rechten des
                // Portals laufen; so bleibt es ein PDF oder ein leerer Rahmen.
                created = URL.createObjectURL(
                    kind === 'pdf' ? new Blob([blob], { type: 'application/pdf' }) : blob,
                );
                setUrl(created);
            })
            .catch((error: any) => {
                // Eine fehlende Vorschau ist kein Fehler, den jemand beheben müsste — sie wird
                // gemeldet, wo sie hingehört, und nicht als Meldung über die Seite gelegt.
                console.error('Could not load the preview', error);
                if (!cancelled) setFailed(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            // Der Verweis muss zurückgegeben werden, sonst hält der Browser die Datei im Speicher,
            // solange die Seite offen ist — bei jedem Klick auf eine andere Datei eine mehr.
            if (created) URL.revokeObjectURL(created);
        };
    }, [path, kind, authentication?.customer]);

    if (!kind) return <></>;

    if (loading) {
        return (
            <div
                className={cn(
                    'flex items-center justify-center rounded-md border bg-muted/30',
                    fill ? 'h-full' : 'h-32',
                )}
            >
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
            </div>
        );
    }

    if (failed || !url) {
        return failed ? (
            <div className="rounded-md border bg-muted/30 p-2 text-center text-xs text-muted-foreground">
                {t('portfolioDashboard.dataRoom.previewFailed')}
            </div>
        ) : (
            <></>
        );
    }

    const preview = (
        <div className={cn('overflow-hidden rounded-md border bg-muted/30', fill && 'h-full')}>
            {kind === 'image' ? (
                <img src={url} alt="" className={cn('w-full object-contain', fill ? 'h-full' : 'max-h-64')} />
            ) : (
                /* Die erste Seite, ohne Bedienleiste: Das Ding ist ein Vorschaubild und kein
                   Lesegerät — dafür ist die Datei zu öffnen. */
                <iframe
                    src={`${url}#toolbar=0&navpanes=0&view=FitH`}
                    title={t('portfolioDashboard.dataRoom.preview')}
                    className={cn('w-full', fill ? 'h-full' : 'h-64')}
                    /* Sonst fängt das eingebettete Dokument den Klick ab und der Rahmen darum
                       erfährt nie davon. Gelesen wird im Betrachter, nicht hier. */
                    style={onOpen ? { pointerEvents: 'none' } : undefined}
                />
            )}
        </div>
    );

    if (!onOpen) return preview;

    /**
     * Ein Feld mit Knopfrolle, kein `<button>`.
     *
     * In der Vorschau steckt für ein PDF ein `<iframe>`, und ein Knopf darf kein interaktives
     * Element enthalten. Der Browser richtet solches Geschachtel auf seine Weise ein — und was
     * React danach vor sich zu haben glaubt, stimmt mit dem Baum im Browser nicht mehr überein.
     * Ein `div` mit Rolle und Tastaturbedienung leistet dasselbe, ohne diese Frage aufzuwerfen.
     */
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => onOpen(url)}
            onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;

                event.preventDefault();
                onOpen(url);
            }}
            title={t('portfolioDashboard.dataRoom.openDocument')}
            aria-label={t('portfolioDashboard.dataRoom.openDocument')}
            className={cn(
                'group block w-full cursor-zoom-in overflow-hidden rounded-md text-left',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                fill && 'h-full',
            )}
        >
            {preview}
        </div>
    );
}
